import { Component, EventEmitter, Input, Output } from '@angular/core';
import { brandInk, PreviewBoton, PreviewVM } from './preview-vm';

export type PreviewModo = 'login' | 'botonera';

/**
 * Preview en vivo del botón nativo (login + botonera). HTML/CSS puro que espeja los Composables
 * reales (`DefaultLoginTemplate`, `DefaultPrincipalesTemplate`, `LanusFrame`, `LujanTemplate`,
 * `EzeizaTemplate`, `PanicButton`, `MainShell.NavigationBar`). Reemplaza el placeholder del
 * provisionador. NO es el render del nativo — es una aproximación para diseñar el cliente.
 */
@Component({
  selector: 'app-provisionador-preview',
  templateUrl: './provisionador-preview.component.html',
  styleUrls: ['./provisionador-preview.component.scss'],
  standalone: false,
})
export class ProvisionadorPreviewComponent {
  @Input() vm!: PreviewVM;
  /** Modo controlado por el padre (se sincroniza con el template que se está editando). */
  @Input() modo: PreviewModo = 'login';
  @Output() modoChange = new EventEmitter<PreviewModo>();
  /**
   * Cliente custom (template hand-made por bundleId): la botonera no se previsualiza. Muestra SOLO
   * el login y oculta el toggle de botonera.
   */
  @Input() soloLogin = false;
  /** Solo botonera (el login es cliente-level y se previsualiza aparte). Oculta el toggle de login. */
  @Input() soloBotonera = false;

  setModo(m: PreviewModo): void {
    this.modo = m;
    this.pagina = 1;
    this.modoChange.emit(m);
  }

  readonly ICON: Record<string, string> = {
    Alerta: '⚠',
    Evento: '📋',
    Sirena: '🔊',
    Reflector: '💡',
    '911': '📞',
    Link: '🔗',
    'Alerta por Punto': '📍',
    Telemedicina: '✚',
    Menu: '≡',
  };

  // Piezas conceptuales de la fila reflector/sirena/chat (en el nativo son botones de la categoría).
  readonly reflector: PreviewBoton = { nombre: 'Reflector', funcion: 'Reflector', color: '#475467' };
  readonly sirena: PreviewBoton = { nombre: 'Sirena', funcion: 'Sirena', color: '#b42318' };
  readonly chatBoton: PreviewBoton = { nombre: 'Chat', funcion: 'Link', color: '#039855' };

  /** Placeholders genéricos (de boton-docs/_generico) para mostrar la plantilla aunque falten imágenes. */
  private readonly GEN = 'assets/preview-generico/';

  /** URL del cliente si la subió; si no, el genérico del slot (banner/hero/logo se ven igual). */
  img(url: string | undefined, slot: string): string {
    return url || this.GEN + slot + '.png';
  }

  /** Página actual de la botonera (1 = principales, 2 = secundarios). */
  pagina = 1;

  /** Contenido de muestra POR plantilla (para mostrar lo que da cada una cuando no hay botones). */
  private readonly DEMO: Record<string, { p: PreviewBoton[]; s: PreviewBoton[] }> = {
    grid: {
      p: [
        { nombre: 'Policía', funcion: '911', color: '#1d4ed8' },
        { nombre: 'Bomberos', funcion: 'Alerta', color: '#b91c1c' },
        { nombre: 'Ambulancia', funcion: 'Telemedicina', color: '#15803d' },
      ],
      s: [
        { nombre: 'Recolección de Ramas', funcion: 'Evento', color: '#b45309' },
        { nombre: 'Reporte de Accidente', funcion: 'Evento', color: '#0e7490' },
      ],
    },
    centrado: {
      p: [{ nombre: 'Violencia de Género', funcion: 'Alerta', color: '#7c2d92' }],
      s: [],
    },
    hub: {
      p: [
        { nombre: 'Emergencias', funcion: 'Menu', color: '#b91c1c' },
        { nombre: 'Servicios', funcion: 'Menu', color: '#15803d' },
        { nombre: 'Información', funcion: 'Menu', color: '#0e7490' },
      ],
      s: [],
    },
  };

  /** Sin botones reales en ningún grupo → modo demo (muestra el potencial de la plantilla). */
  get esDemo(): boolean {
    return (
      !this.vm?.principales?.length &&
      !this.vm?.secundarios?.length &&
      !this.vm?.otros?.length
    );
  }
  private get demoSet(): { p: PreviewBoton[]; s: PreviewBoton[] } {
    const id = this.vm?.botoneraId;
    return this.DEMO[id === 'centrado' || id === 'hub' ? id : 'grid'];
  }
  get pPrincipales(): PreviewBoton[] {
    return this.esDemo ? this.demoSet.p : this.vm.principales;
  }
  get pSecundarios(): PreviewBoton[] {
    return this.esDemo ? this.demoSet.s : this.vm.secundarios;
  }
  /** En demo mostramos reflector/sirena y chat para ilustrar las "acciones". */
  get mostrarSirenas(): boolean {
    return !!this.vm?.sirenas || this.esDemo;
  }
  get mostrarChat(): boolean {
    return !!this.vm?.chat || this.esDemo;
  }
  /** Botones de la página actual. */
  get botonesActuales(): PreviewBoton[] {
    return this.pagina === 2 ? this.pSecundarios : this.pPrincipales;
  }
  /** Mostrar "Más" (pág 1 → 2) si hay secundarios (reales o de muestra). */
  get hayMas(): boolean {
    return this.pSecundarios.length > 0;
  }

  setPagina(n: number): void {
    this.pagina = n;
  }

  ico(funcion?: string): string {
    return (funcion && this.ICON[funcion]) || '⚠';
  }

  ink(color?: string): string {
    return brandInk(color);
  }

  /** Altura del botón principal según cantidad (2 grande, 3 medio, 4+ chico). El caso de 1
   *  botón se renderiza cuadrado aparte. Regla universal a todas las botoneras. */
  alturaPrincipal(n: number): number {
    return n <= 2 ? 96 : n <= 3 ? 76 : 54;
  }
}
